import { MyClubFilterType } from "@/hooks/useMyClubFilter";
import { club as clubIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { FavoritesSheet } from "@/pages/FrontPage/ForYou/FavoritesSheet";
import { initFavoritesFlow } from "@/redux/frontPage/frontPageSlice";
import {
  clearFilter,
  ownedOrEvolvedSelector,
  playersSelector,
  setFilterValues,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import {
  userFavoriteClubIdsSelector,
  userFavoriteCountryIdSelector,
} from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { Dna, ShieldHalf, Star } from "lucide-react";
import { ReactNode, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  SelectorFilterType,
  UpdateFilterType,
} from "./FiltersPanel/filterTypes";

export const QuickFilter = ({
  label,
  icon,
  selected,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex h-8 shrink-0 items-center gap-x-1.5 rounded-full border px-3 text-xs font-semibold whitespace-nowrap transition-colors",
      selected
        ? "border-primary-border bg-blue-900/50 text-foreground"
        : "border-edge-strong bg-surface text-foreground-muted hover:bg-surface-raised hover:text-foreground",
    )}
  >
    <span className="flex size-4 items-center justify-center">{icon}</span>
    {label}
  </button>
);

const sameIds = (a: number[], b: number[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

/** One-tap filters above the list: club import plus the user's favorites. */
export const QuickFilters = ({
  onMyClubClicked,
  onFiltersChanged,
}: {
  onMyClubClicked: (f: UpdateFilterType, type: MyClubFilterType) => void;
  /** Runs after a favorite filter is set or cleared. */
  onFiltersChanged: () => void;
}) => {
  const { t } = useTranslation("players");
  const dispatch = useDispatch<AppDispatch>();
  const owned = useSelector(ownedOrEvolvedSelector);
  const { playerFilters } = useSelector(playersSelector);
  const favoriteClubIds = useSelector(userFavoriteClubIdsSelector);
  const favoriteCountryId = useSelector(userFavoriteCountryIdSelector);
  const [sheetOpen, setSheetOpen] = useState(false);

  const selected = (key: string) =>
    (playerFilters.find((f) => f.key === key) as SelectorFilterType)
      ?.selectedOptions ?? [];
  const favoriteCountryIds = favoriteCountryId ? [favoriteCountryId] : [];
  const clubOn =
    favoriteClubIds.length > 0 && sameIds(selected("club"), favoriteClubIds);
  const countryOn =
    favoriteCountryIds.length > 0 &&
    sameIds(selected("country"), favoriteCountryIds);

  // No favorites saved yet: the front page's picker sets them up.
  const toggleFavorite = (
    key: "club" | "country",
    ids: number[],
    on: boolean,
  ) => {
    if (ids.length === 0) {
      dispatch(
        initFavoritesFlow({
          clubIds: favoriteClubIds,
          countryId: favoriteCountryId ?? null,
        }),
      );
      setSheetOpen(true);
      return;
    }
    dispatch(on ? clearFilter(key) : setFilterValues({ key, values: ids }));
    onFiltersChanged();
  };

  const ownedTiles = [
    {
      value: 0,
      type: "own-player",
      category: "Owned",
      label: t("filters.ownedOrEvolved.myClub"),
      icon: <div className="size-4">{clubIcon}</div>,
    },
    {
      value: 1,
      type: "own-evo-player",
      category: "Evolved",
      label: t("filters.ownedOrEvolved.myEvolutions"),
      icon: <Dna className="size-3.5" />,
    },
    {
      value: 2,
      type: "active-squad",
      category: "ActiveSquad",
      label: t("filters.ownedOrEvolved.squad"),
      icon: <ShieldHalf className="size-3.5" fill="currentColor" />,
    },
  ] as const;

  return (
    <>
      <div className="flex items-center gap-x-2">
        {ownedTiles.map((tile) => (
          <QuickFilter
            key={tile.value}
            label={tile.label}
            icon={tile.icon}
            selected={owned?.category === tile.category}
            onClick={() =>
              onMyClubClicked(
                { key: "ownedOrEvolved", value: tile.value },
                tile.type,
              )
            }
          />
        ))}
        <QuickFilter
          label={t("filters.quick.favoriteClub")}
          icon={
            <Star
              className="size-3.5"
              fill={clubOn ? "currentColor" : "none"}
            />
          }
          selected={clubOn}
          onClick={() => toggleFavorite("club", favoriteClubIds, clubOn)}
        />
        <QuickFilter
          label={t("filters.quick.favoriteCountry")}
          icon={
            <Star
              className="size-3.5"
              fill={countryOn ? "currentColor" : "none"}
            />
          }
          selected={countryOn}
          onClick={() =>
            toggleFavorite("country", favoriteCountryIds, countryOn)
          }
        />
      </div>
      <FavoritesSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
};
